import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export type GuestBenefitsDetails = {
  name: string;
  email: string;
};

interface GuestBenefitsPromptProps {
  show: boolean;
  onContinue: (details: GuestBenefitsDetails) => Promise<void>;
  onClose?: () => void;
}

const GuestBenefitsPrompt = ({
  show,
  onContinue,
  onClose,
}: GuestBenefitsPromptProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState("");
  const titleRef = useRef<HTMLHeadingElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!show) {
      setName("");
      setEmail("");
      setError("");
    }
  }, [show]);

  const handleClose = () => {
    if (isStarting) return;
    if (onClose) {
      onClose();
    } else {
      navigate("/");
    }
  };

  const continueToBenefits = async (details: GuestBenefitsDetails) => {
    if (isStarting) return;
    setIsStarting(true);
    setError("");

    try {
      await onContinue(details);
    } catch (startError) {
      setError(
        startError instanceof Error
          ? startError.message
          : "We couldn’t start your benefits screening. Please try again."
      );
      setIsStarting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedName || !nameInputRef.current?.checkValidity()) {
      nameInputRef.current?.focus();
      nameInputRef.current?.reportValidity();
      return;
    }

    if (!normalizedEmail || !emailInputRef.current?.checkValidity()) {
      emailInputRef.current?.focus();
      emailInputRef.current?.reportValidity();
      return;
    }

    continueToBenefits({
      name: normalizedName,
      email: normalizedEmail,
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onEntered={() => titleRef.current?.focus()}
      centered
      id="guest-benefits-modal"
      aria-labelledby="guest-benefits-modal-title"
      aria-describedby="guest-benefits-modal-description"
      backdrop="static"
      keyboard={!isStarting}
    >
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!isStarting}>
          <Modal.Title
            as="h2"
            id="guest-benefits-modal-title"
            ref={titleRef}
            tabIndex={-1}
          >
            Before you start
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p id="guest-benefits-modal-description">
            Name and email are required. Sharing them helps us understand who we’re
            supporting.
          </p>

          <label htmlFor="guest-benefits-name">
            Name <span aria-hidden="true">*</span>
          </label>
          <input
            ref={nameInputRef}
            id="guest-benefits-name"
            className="form-control"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            disabled={isStarting}
            required
            aria-required="true"
          />

          <label htmlFor="guest-benefits-email">
            Email address <span aria-hidden="true">*</span>
          </label>
          <input
            ref={emailInputRef}
            id="guest-benefits-email"
            className="form-control"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            disabled={isStarting}
            required
            aria-required="true"
          />

          {error && <p className="guest-benefits-error" role="alert">{error}</p>}

          <div className="guest-benefits-actions">
            <button type="submit" disabled={isStarting} aria-busy={isStarting}>
              {isStarting ? "Starting…" : "Continue"}
            </button>
          </div>
        </Modal.Body>
      </form>
    </Modal>
  );
};

export default GuestBenefitsPrompt;
